import { Plus, Printer, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../AuthContext";
import { EmptyState } from "../components/EmptyState";
import { Toast } from "../components/Toast";
import type { InventoryUnit } from "../types";
import { inventoryAppearance } from "../inventoryDisplay";
import { InventoryVisual } from "../components/InventoryVisual";

export function InventoryListPage() {
  const { user } = useAuth();
  const [query, setQuery] = useState("");
  const [units, setUnits] = useState<InventoryUnit[]>([]);
  const [loading, setLoading] = useState(true);
  const [printingId, setPrintingId] = useState("");
  const [toast, setToast] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setLoading(true);
      api
        .listInventory(query)
        .then(setUnits)
        .finally(() => setLoading(false));
    }, 180);
    return () => window.clearTimeout(timeout);
  }, [query]);

  return (
    <div className="page">
      <div className="page-heading">
        <div>
          <h1>Inventory</h1>
          <p>Search stock numbers, names, contents, and notes.</p>
        </div>
        <div className="heading-actions">
          <a
            className="button secondary"
            href="/api/inventory/thermal-labels.pdf"
            target="_blank"
            rel="noreferrer"
          >
            <Printer size={18} />
            Open all label PDFs
          </a>
          <Link className="button primary" to="/inventory/new">
            <Plus size={18} /> New inventory
          </Link>
        </div>
      </div>
      <label className="search-field">
        <Search size={20} />
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search inventory"
          inputMode="search"
        />
      </label>
      {loading ? (
        <div className="loading-row">Loading inventory…</div>
      ) : units.length ? (
        <div className="unit-list">
          {units.map((unit) => (
            <div className="unit-row" key={unit.id}>
              <Link className="unit-row-link" to={`/inventory/${unit.id}`}>
                <div className="unit-thumbnail">
                  <InventoryVisual unit={unit} />
                </div>
                <div className="unit-row-main">
                  <strong>{unit.name}</strong>
                  <span>{unit.stock_number}</span>
                  {inventoryAppearance(unit) && <small>{inventoryAppearance(unit)}</small>}
                </div>
                <div className="unit-row-place">
                  <span>{unit.current_location?.name || "Location not set"}</span>
                  <small>to {unit.destination_room?.name || "destination not set"}</small>
                </div>
                <span className="status">{unit.move_status}</span>
              </Link>
              {user?.role !== "viewer" && (
                <button
                  className="unit-row-print"
                  aria-label={`Print label for ${unit.stock_number}`}
                  disabled={printingId === unit.id}
                  onClick={async () => {
                    setPrintingId(unit.id);
                    try {
                      const result = await api.printInventoryLabel(unit.id);
                      setToast(`${unit.stock_number}: ${result.message}`);
                    } catch (reason) {
                      setError(
                        reason instanceof Error ? reason.message : "The label could not be sent"
                      );
                    } finally {
                      setPrintingId("");
                    }
                  }}
                >
                  <Printer />
                  <span>{printingId === unit.id ? "Sending…" : "Print"}</span>
                </button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <EmptyState
          title={query ? "No matching inventory" : "No inventory yet"}
          description={query ? "Try a different stock number, name, or keyword." : "Create your first tagged moving unit."}
        />
      )}
      {toast && <Toast message={toast} tone="success" onClose={() => setToast("")} />}
      {error && <Toast message={error} tone="error" onClose={() => setError("")} />}
    </div>
  );
}
